import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { ClassInfoType } from '../../types'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import classes from './index.module.css'
import { InputNew } from '@UI/InputNew'
import cn from 'classnames'
import { Select } from 'antd'
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons'
import Button from '@UI/New/Button'
import CreateEditFolderModal from '@pages/Admin/Manage/tabs/ClassesPage/components/CreateEditFolderModal/CreateEditFolderModal'
import { GeneralCourseInformation, RefetchGeneralDataVariantType } from '../..'
import CourseList from '../CourseList/CourseList'
import { classApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { deepEqualObject } from '@/helpers/deepEqualObject'
import ClassSettings, { UnsavedChanges } from '../ClassSettings/ClassSettings'
import { useHistory } from 'react-router-dom'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { closePreloader, openPreloader } from '@store/preloaderSlice'
import { useDispatch } from 'react-redux'

interface PageContentProps {
  classInfo: ClassInfoType
  generalData: GeneralCourseInformation
  refetchGeneralData: (variant: RefetchGeneralDataVariantType) => void
  unsavedChanges?: boolean
  setUnsavedChanges?: (value: boolean) => void
  hiddenParts?: {
    main?: boolean
    courses?: boolean
    settings?: boolean
  }
}

export interface PageContentRef {
  handleSaveClass: ({ onSuccessCallback }: { onSuccessCallback?: () => void }) => void
  unsavedChangesBeforeSaving: UnsavedChanges
  isSaving: boolean
}

type DeepNullableOptional<T> = {
  [K in keyof T]?: T[K] extends object
    ? DeepNullableOptional<T[K]>
    : T[K] | null
}

type CustomGroupType = ClassInfoType['groups'][number] & {
  key: string
}

export type ElClassFormDataType = DeepNullableOptional<
  Omit<ClassInfoType, 'groups'> & {
    mass_payment_amount: boolean
    groups: CustomGroupType[]
  }
>

const preparationElClassData = (classInfo: ClassInfoType) => ({
  ...classInfo,
  mass_payment_amount: true,
  changeVksLinkForEveryone: false,
  groups: classInfo?.groups?.map((group, i) => ({
    ...group,
    key: `${group.id}_${i}`,
  })),
})

const PageContent = forwardRef<PageContentRef, PageContentProps>(
  (
    {
      classInfo,
      generalData,
      refetchGeneralData,
      unsavedChanges,
      setUnsavedChanges,
      hiddenParts,
    },
    ref,
  ) => {
    const addAlert = useAddAlert()
    const history = useHistory()
    const dispatch = useDispatch()
    const [openCreateFolderModal, setOpenCreateFolderModal] = useState(false)
    const [allFolders, setAllFolders] = useState(generalData.allFolders)

    const initElClassFormData = useMemo(
      () => preparationElClassData(classInfo),
      [classInfo],
    )

    const [elClassFormData, setElClassFormData] =
      useState<ElClassFormDataType>(initElClassFormData)

    useEffect(() => {
      setElClassFormData(preparationElClassData(classInfo))
    }, [classInfo])

    console.log('elClassFormData', elClassFormData)

    const [unsavedChangesBeforeSaving, setUnsavedChangesBeforeSaving] =
      useState<UnsavedChanges>([])
    const [saved, setSaved] = useState(false)

    const handlerData = (key: keyof ElClassFormDataType, value: any) => {
      setElClassFormData((prev) => ({ ...prev, [key]: value }))
    }

    const handleSaveClass = ({ onSuccessCallback }: { onSuccessCallback?: () => void }) => {
      setSaved(true)
      dispatch(openPreloader({text: 'Идет сохранение'}))
      classApi
        .update(elClassFormData.id, {
          title: elClassFormData.title,
          one_pay: elClassFormData.one_pay,
          mass_add_group: elClassFormData.mass_add_group,
          ignore_payment: elClassFormData.ignore_payment,
          mass_payment_amount: false, // вынесен в отдельный ендпоинт
          amount: elClassFormData.one_pay ? elClassFormData.amount ?? 0 : 0,
          link_vks: elClassFormData.link_vks ?? null,
          change_vks_link_for_everyone:
            elClassFormData.change_vks_link_for_everyone ?? false,
          folder_id: elClassFormData.folder_id,
          groups: elClassFormData.groups?.map((group) => ({
            group_id: group?.id,
            tariff_id: group?.tariff_id,
          })),
          orders_group: elClassFormData.orders_group,
          pointer: elClassFormData.pointer,
          auto_access_duration: elClassFormData.auto_access_duration ?? null,
          auto_access_unit: elClassFormData.auto_access_unit ?? 'months',
          tag: elClassFormData?.tag ?? null,
          direct_link_enabled: elClassFormData.direct_link_enabled ?? false,
        })
        .then((resDef) => {
          const res = resDef as any
          if (res.response?.status >= 400) {
            addAlert({
              type: 'error',
              title: res.response?.data?.error,
              text: Object.entries(res.response?.data?.data)[0][1],
            })
          } else {
            // refetchGeneralData('class')
            addAlert({ title: 'Успешно', text: 'Изменения сохранены' })
            onSuccessCallback ? onSuccessCallback() : history.push('/manage/classes')
          }
        })
        .catch((err) => {
          addAlert({
            type: 'error',
            title: 'Ошибка',
            text: 'Не удалось сохранить изменения',
          })
        })
        .finally(() => {
          setSaved(false)
          dispatch(closePreloader())
        })
    }

    useImperativeHandle(ref, () => ({
      handleSaveClass,
      unsavedChangesBeforeSaving,
      isSaving: saved,
    }))

    useEffect(() => {
      setUnsavedChanges?.(
        !deepEqualObject(initElClassFormData, elClassFormData),
      )
    }, [initElClassFormData, elClassFormData])

    return (
      <>
        <div className={classes.mainWrapper}>
          {hiddenParts?.main !== true && (
            <div className={classes.section}>
              <div className={classes.sectionContentGrid2}>
                <div className={classes.fieldWrapper}>
                  <p className={mclasses.label}>
                    Название класса/обр. Траектории
                  </p>
                  {/*@ts-ignore*/}
                  <InputNew
                    className={cn(iclasses.newInputDesign, classes.inputField)}
                    placeholder={'Название правила'}
                    value={elClassFormData.title}
                    onChange={(e: any) =>
                      handlerData('title', e.currentTarget.value)
                    }
                    validate={(elClassFormData?.title ?? '').length < 1}
                    errorText={'Поле не может быть пустым'}
                    typeValid="custom"
                  />
                </div>
                <div className={classes.fieldWrapper}>
                  <p className={mclasses.label}>
                    Папка, в которой лежит класс/обр. Траектория
                  </p>
                  {allFolders.length === 0 ? (
                    <Button
                      color="secondary"
                      style={{ width: '100%', height: '100%' }}
                      onClick={() => setOpenCreateFolderModal(true)}
                      leftContent={
                        <PlusOutlined
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      }
                    >
                      Создать новую папку
                    </Button>
                  ) : (
                    <Select
                      className={cn(iclasses.newSelect, classes.selectField)}
                      popupClassName={iclasses.newSelectPopup}
                      placeholder="Выберите папку"
                      value={elClassFormData?.folder_id}
                      onChange={(value) => handlerData('folder_id', value)}
                      options={allFolders.map((folder) => ({
                        label: folder.title,
                        value: folder.id,
                      }))}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {hiddenParts?.courses !== true && (
            <div className={classes.section}>
              <div className={classes.sectionTitleWrapper}>
                <p className={classes.sectionTitle}>Список курсов в классе</p>
                <div className={classes.tooltipWrapper}>
                  <TooltipWrapper
                    title={
                      'Все курсы, которые Вы добавляете в класс автоматически будут доступны учащемуся при добавлении данного класса'
                    }
                    className={classes.tooltipMain}
                    overlayClassName={classes.tooltipOverlay}
                  >
                    <InfoCircleOutlined
                      className={classes.tooltipIcon}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  </TooltipWrapper>
                </div>
              </div>
              <CourseList
                generalData={generalData}
                elClassFormData={elClassFormData}
                setElClassFormData={setElClassFormData}
                refetchGeneralData={refetchGeneralData}
              />
            </div>
          )}

          {hiddenParts?.settings !== true && (
            <div className={classes.section}>
              <p className={classes.sectionTitle}>Настройки класса</p>
              <ClassSettings
                generalData={generalData}
                elClassFormData={elClassFormData}
                setElClassFormData={setElClassFormData}
                refetchGeneralData={refetchGeneralData}
                //
                setOtherUnsavedChanges={setUnsavedChangesBeforeSaving}
              />
            </div>
          )}
        </div>

        {/* Modals */}
        <CreateEditFolderModal
          open={openCreateFolderModal}
          onClose={() => setOpenCreateFolderModal(false)}
          onSuccess={(newFolder) => {
            setAllFolders((prev) => [...prev, newFolder])
            handlerData('folder_id', newFolder.id)
            setOpenCreateFolderModal(false)
          }}
        />
      </>
    )
  },
)

export default PageContent